import { useMemo } from 'react';

import { IEvent } from '~/Models/DTO/Event.model';
import { ICalendarOffset } from '~/Screens/Calendar/interfaces';
import {
  MIN_CALENDAR_EVENT_HEIGHT,
  TIME_STEP,
  TIME_STEPS_NUMBER_PER_DAY,
} from '~/Screens/Calendar/constants';
import { TIME_VALUES } from '~/Constants/Date.constants';
import { getEventTimeInSeconds } from '~/Screens/Calendar/CalendarView/components';

/*
find width and horizontal position
width - number of units to divide container width by
horizontal position - pixel offset from left
*/
const getAttributes = ({ events, collisions }: { events: any; collisions: number[][] }) => {
  const width = Array(events.length).fill(0);
  const leftOffSet = Array(events.length).fill(0);

  collisions?.forEach((period) => {
    // number of events in that period
    const count = period?.reduce((a, b) => (b ? a + 1 : a), 0);

    if (count > 1) {
      period.forEach((event, id) => {
        // max number of events it is sharing a time period with determines width
        if (period[id]) {
          if (count > width[id]) {
            width[id] = count;
          }
        }

        if (period[id] && !leftOffSet[id]) {
          leftOffSet[id] = period[id];
        }
      });
    }
  });

  return { width, leftOffSet };
};

interface IRelativeEventTime {
  start: number;
  end: number;
  diff: number;
}

const getCollisions = (events: { event: IEvent; time: IRelativeEventTime }[]) => {
  const collisions = Array.from({ length: TIME_STEPS_NUMBER_PER_DAY }, () =>
    Array(events.length).fill(0),
  );

  events?.forEach(({ time }, id: number) => {
    const end = time.end > TIME_VALUES.DAY ? TIME_VALUES.DAY - 1 : time.end - 1;
    let start = time.start;
    let order = 1;

    while (start < end) {
      const timeIndex = Math.floor(start / TIME_STEP);

      while (order < events.length) {
        if (!collisions[timeIndex].includes(order)) {
          break;
        }
        order++;
      }

      collisions[timeIndex][id] = order;
      start += TIME_STEP;
    }

    collisions[Math.floor(end / TIME_STEP)][id] = order;
  });

  return collisions;
};

interface IUseCalendarDayView {
  daysOffset: number;
  offset: ICalendarOffset;
  events: IEvent[];
}

export const useCalendarDay = ({ daysOffset, offset, events }: IUseCalendarDayView) => {
  const sortedEvents = useMemo(
    () =>
      events
        ?.map((event) => {
          const { start, dayStart, diff, end } = getEventTimeInSeconds(event);

          return {
            event,
            time: {
              end: end - dayStart,
              diff,
              start: start - dayStart,
            },
          };
        })
        .sort((first, second) => first.time.start - second.time.start),
    [events],
  );
  const eventsData = useMemo(() => {
    if (!sortedEvents || !offset.height) {
      return null;
    }
    const collisions = getCollisions(sortedEvents);
    const { width, leftOffSet } = getAttributes({ collisions, events: sortedEvents });

    return sortedEvents?.map(
      ({ time, event }: { event: IEvent; time: IRelativeEventTime }, idx: number) => {
        const height = (time.diff / TIME_VALUES.DAY) * offset.height;
        const top = (time.start / TIME_VALUES.DAY) * offset.height;
        const units = width[idx] || 1;

        let left = (offset.width / width[idx]) * (leftOffSet[idx] - 1);
        if (!left || left < 0) {
          left = 0;
        }

        return {
          event,
          position: {
            top,
            left,
            width: offset.width / units,
            height: height < MIN_CALENDAR_EVENT_HEIGHT ? MIN_CALENDAR_EVENT_HEIGHT : height,
            daysOffset,
          },
        };
      },
    );
  }, [sortedEvents, offset, daysOffset]);

  return {
    eventsData,
  };
};
